import * as ng from 'angular';
import * as uib from 'angular-ui-bootstrap';
import * as _ from 'underscore';

const fieldSettingsStandardViewUrl = require('./form-field-settings-standard.html');
const fieldSettingsSurveyViewUrl = require('./form-field-settings-survey.html');
const fieldSettingsTextViewUrl = require('./form-field-settings-text.html');
const fieldSettingsOptInViewUrl = require('./form-field-settings-optin.html');

import { L10nService, propertyParser, UtilsService } from '../../../../shared';
import { defaults, FormEditorController } from './form-editor.component';
import { SurveyItemsApiService } from '../../../survey';
import { ExperienceTypeID } from '../..';

export class FormFieldSettingsController {
   initializing = true;
   saving = false;

   configTitle:string;
   template:string;

   language:string;
   languageName:string;
   languages:string[];

   choiceDeleteQueue = [];

   field;

   readonly gridsterConfig = {
      pushing: true,
      floating: true,
      swapping: true,
      maxRows: 100000,
      columns: 1,
      rowHeight: 41,
      mobileModeEnabled: false,
      margins: [5, 0],
      resizable: {
         enabled: false
      },
      draggable: {
         handle: '.drag'
      }
   };

   static $inject = [
      '$q', '$uibModalInstance',
      'utils', 'l10n', 'surveyItemsApi', 'toast', 'formEditor', 'field'
   ];

   constructor(
      private $q:ng.IQService,
      private $uibModalInstance:uib.IModalInstanceService,
      private utils:UtilsService,
      l10n:L10nService,
      private surveyItemsApi:SurveyItemsApiService,
      private toast,
      private formEditor:FormEditorController,
      field
   ) {
      this.language = formEditor.language;
      this.languageName = l10n.getName(this.language);
      this.languages = formEditor.languages;

      this.field = ng.copy(field);

      if (this.field.type == 'textLabel') {
         this.field.description[this.language] = l10n.getTranslatedConfig(this.field.description, this.language);
      }

      if (utils.isBoolean(this.field.value)) {
         this.field.value = utils.parseBoolean(this.field.value);

      }

      if (formEditor.isStandard(this.field.key, this.field.type)) {
         this.configTitle = 'Standard Field Configuration';
         this.template = fieldSettingsStandardViewUrl;
         this.initializing = false;

      } else if (formEditor.isSurvey(this.field.type)) {
         let fieldName = 'Survey Item';
         if (this.field.type === 'surveyBoolean') {
            fieldName = 'Checkbox (True/False)';
         } else if (this.field.type === 'surveyText') {
            fieldName = 'Text Input';
         } else if (this.field.type === 'surveyOption') {
            fieldName = 'Single Select';
         } else if (this.field.type === 'surveyMultioption') {
            fieldName = 'Multi Select';
         }

         this.configTitle = fieldName + ' Settings';
         this.template = fieldSettingsSurveyViewUrl;

         surveyItemsApi
            .get(this.field.survey.id)
            .then(res => {
               _.each(res.surveyItemChoices as any[], item => {
                  item.col = 0;
                  item.sizeX = 1;
                  item.sizeY = 1;
               });

               this.field.survey = res;
            })
            .finally(() => {
               this.initializing = false;
            });

      } else if (formEditor.isTextLabel(this.field.type)) {
         this.configTitle = 'Text Label Configuration';
         this.template = fieldSettingsTextViewUrl;
         this.initializing = false;
      } else if (this.field.type == 'optin') {
         this.template = fieldSettingsOptInViewUrl;
         this.initializing = false;
      }
   }

   get tinymceOptions() {
      return this.formEditor.tinymceOptions;
   }

   get isApp() {
      let experienceType = this.formEditor.editorPageCtrl.editorCtrl.model.typeDef;
      return experienceType.id == ExperienceTypeID.App;
   }

   get isRegSite() {
      let experienceType = this.formEditor.editorPageCtrl.editorCtrl.model.typeDef;
      return experienceType.id == ExperienceTypeID.RSVP || experienceType.id == ExperienceTypeID.WebReg;
   }

   get fieldTitle() {
      return defaults.strings.titles[this.field.key];
   }

   get showTerms() {
      let keys = ['contactOptin', 'promoOptin', 'contestOptin', 'salesOptin', 'termsAgree'];
      return keys.indexOf(this.field.key) != -1;
   }

   get showRequired() {
      let keys = ['checkbox', 'surveyBoolean', 'textLabel'];
      return keys.indexOf(this.field.type) == -1;
   }

   get disableRequired() {
      let keys = ['firstName', 'lastName', 'email', 'confirmEmail'];
      return keys.indexOf(this.field.key) != -1;
   }

   get termsFile() {
      let uploads = this.formEditor.editorPageCtrl.editorCtrl.model.settings.uploads;
      if (this.field.tosKey && uploads[this.field.tosKey]) {
         return uploads[this.field.tosKey][this.language];
      }
   }

   setTerms(file) {
      let uploads = this.formEditor.editorPageCtrl.editorCtrl.model.settings.uploads;

      if (!this.field.tosKey) {
         this.field.tosKey = this.utils.generateUniqueId();
      }

      this.utils.readFileAsDataUrl(file).then(dataUrl => {
         propertyParser(uploads, this.field.tosKey + '.' + this.language)
            .set(dataUrl);
      });
   }

   save() {
      if (this.field.infoURL &&
         this.field.infoURL[this.language] &&
         !this.utils.isValidURL(this.field.infoURL[this.language])
      ) {
         this.toast.error()
            .message('Please enter a valid URL (starting with http:// or https://)')
            .send();

         return;
      }

      if (this.formEditor.isSurvey(this.field.type)) {
         this.saving = true;

         this.surveyItemsApi.update(this.field.survey)
            .then(() => this.deleteChoices())
            .then(() => this.editChoices())
            .then(() => {
               this.$uibModalInstance.close(this.field);
            })
            .catch(err => {
               this.toast.error()
                  .title('There was a problem updating the survey question')
                  .formErrors(err)
                  .send();
            }).finally(() => {
               this.saving = false;
            });
      } else {
         this.$uibModalInstance.close(this.field);
      }
   }

   private deleteChoices() {
      return this.$q.all(this.choiceDeleteQueue.map(choice => {
         choice.surveyItemId = this.field.survey.id;
         return this.surveyItemsApi.removeChoice(choice);
      }));
   }

   private editChoices() {
      this.field.survey.surveyItemChoices = _.sortBy(this.field.survey.surveyItemChoices, 'row');

      return this.$q.all(this.field.survey.surveyItemChoices.map(choice => {
         if (choice.key === null) {
            let existingKeys = this.field.survey.surveyItemChoices.map(c => c.key);

            choice.key = this.surveyItemsApi.keyFromLabel(choice.label[this.language]);
            choice.key = this.surveyItemsApi.uniqueKey(choice.key, existingKeys);
         }

         choice.surveyItemId = this.field.survey.id;

         // Format data returned from Gridster
         choice.index = choice.row;
         delete choice.row;
         delete choice.col;
         delete choice.sizeX;
         delete choice.sizeY;

         if (choice.id) {
            return this.surveyItemsApi.updateChoice(choice);
         } else {
            return this.surveyItemsApi.addChoice(choice);
         }
      }));
   }

   cancel() {
      this.$uibModalInstance.dismiss('cancel');
   }

   range(start, end) {
      return _.range(start, end + 1);
   }

   addChoice() {
      this.field.survey.surveyItemChoices.push({
         label: null,
         key: null,
         row: this.field.survey.surveyItemChoices.length,
         col: 0,
         sizeX: 1,
         sizeY: 1
      });
   }

   deleteChoice(key, choice) {
      if (choice.id) {
         this.choiceDeleteQueue.push(choice);
      }

      this.field.survey.surveyItemChoices.splice(key, 1);
   }

   toggleVisible(key, choice) {
      if (!this.field.hiddenChoices) {
         this.field.hiddenChoices = [];
      }

      if (_.contains(this.field.hiddenChoices, choice.key)) {
         this.field.hiddenChoices = _.without(this.field.hiddenChoices, choice.key);
      } else {
         this.field.hiddenChoices.push(choice.key);
      }
   }

   isChoiceHidden(choice) {
      return _.contains(this.field.hiddenChoices, choice.key);
   }
}

ng.module('e360')
   .controller('FormFieldSettingsController', FormFieldSettingsController);
